/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';

import { FlexyForm, FormObject, FormStructure } from '@europrocurement/flexy-form';
import { FaOptionIcon, publisherIcon } from '@europrocurement/l2d-icons';
import {
    AchatsAppDispatch,
    AchatsStateType,
    CodeRejet,
    CodeRejetApiObject,
    CODES_REJETS_COMPLETE_LIST_DATASOURCE_NAME,
    Fournisseur,
    useAchatsService,
    useSociete,
    useTiersService,
} from '@europrocurement/l2d-domain';
import { ExceptionFacturanteApiPostExceptionFacturanteCollectionRequest } from '@europrocurement/l2d-domain/openApi/ApiAchats';

import {
    DEF_FREQUENCE_RELANCE_ACHATS_DATASOURCE_NAME,
    DefFrequence,
    DefFrequenceApiObject,
} from '@europrocurement/l2d-domain/reducers/achats/slices/defFrequenceSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import {
    useCurrentPublisher,
    usePublisherDatasource,
} from '../../../../../../../../hooks/publisherHooks';
import HeaderStructure from '../../../../../../../Achats/components/fragments/HeaderStructure';

// import { castIriToId } from '../../../../../../../utils/iriParser';

type CreateExceptionFormProps = {
    afterSubmit: () => void;
};

const CreateExceptionForm: React.FunctionComponent<CreateExceptionFormProps> = function ({
    afterSubmit,
}: CreateExceptionFormProps) {
    const { defFrequenceDataSourcesThunks, codeRejetDataSourcesThunks, exceptionFacturanteApi } =
        useAchatsService();

    const { fournisseurDataSourcesThunks } = useTiersService();

    const publisherDatasource = usePublisherDatasource();
    const publisherSelected = useCurrentPublisher();
    const publisherFetchData = fournisseurDataSourcesThunks.main.getData;
    const isPublisherAlreadySelected = !publisherSelected || !publisherSelected.id;

    const rejectCodeCompleteListDatasource = useSelector<AchatsStateType>(
        (s) => s.achats.coderejet[CODES_REJETS_COMPLETE_LIST_DATASOURCE_NAME],
    ) as DataSource<CodeRejetApiObject>;

    const rejectCodeCompleteListFetchData =
        codeRejetDataSourcesThunks[CODES_REJETS_COMPLETE_LIST_DATASOURCE_NAME].data.fetchAll;

    const frequencyRelanceAchatsDatasource = useSelector<AchatsStateType>(
        (s) => s.achats.frequence[DEF_FREQUENCE_RELANCE_ACHATS_DATASOURCE_NAME],
    ) as DataSource<DefFrequenceApiObject>;

    const frequencyRelanceAchatsFetchData =
        defFrequenceDataSourcesThunks[DEF_FREQUENCE_RELANCE_ACHATS_DATASOURCE_NAME].data
            .fetchRelanceAchatsFrequences;

    const initialValues: ExceptionFacturanteApiPostExceptionFacturanteCollectionRequest['postExceptionFacturanteCollectionRequest'] =
        {
            idFacturante: publisherSelected?.id || -1,
            codeRejet: 'ERCA1',
            relanceAuto: false,
            idFrequenceRelance: 1,
        };

    const dispatch = useDispatch<AchatsAppDispatch>();
    const xIdSociete = useSociete();
    const { handleFailure } = useHandleError();

    const handleSubmit = React.useCallback<
        NonNullable<React.ComponentProps<typeof FlexyForm>['onSubmit']>
    >(
        async (values: FormObject) => {
            let fineRejectCode = `/achats/code_rejets/${values.codeRejet}`;
            let fineFrequency = `/achats/def_frequences/${values.idFrequenceRelance}`;

            if (
                typeof values.codeRejet === 'object' &&
                (values.codeRejet as CodeRejet)['@id'] !== undefined
            ) {
                fineRejectCode = (values.codeRejet as CodeRejet)['@id'] as unknown as string;
            }

            if (
                typeof values.idFrequenceRelance === 'object' &&
                (values.idFrequenceRelance as DefFrequence).dfrId !== undefined
            ) {
                fineFrequency = (values.idFrequenceRelance as DefFrequence)
                    .dfrId as unknown as string;
            }

            const requestParameters: ExceptionFacturanteApiPostExceptionFacturanteCollectionRequest =
                {
                    xIdSociete,
                    postExceptionFacturanteCollectionRequest: {
                        idFacturante: values.idFacturante as number,
                        relanceAuto: values.relanceAuto as boolean,
                        idFrequenceRelance: fineFrequency as unknown as number,
                        codeRejet: fineRejectCode,
                    },
                };

            exceptionFacturanteApi
                .postExceptionFacturanteCollection(requestParameters)
                .then((res) => {
                    console.debug(res);
                    if (afterSubmit) {
                        afterSubmit();
                    }
                })
                .catch((error) => {
                    handleFailure({ error, withToaster: true });
                });
        },
        [afterSubmit, exceptionFacturanteApi, handleFailure, xIdSociete],
    );

    const selectPublisherFormStructure: FormStructure = React.useMemo(
        () => ({
            type: 'autocompletestore',
            name: 'idFacturante',
            inputlabel: 'Fournisseur',
            dataSource: publisherDatasource,
            fetchData: publisherFetchData,
            renderField: 'id',
            idProperty: 'id',
            debounce: 1000,
            rules: {
                required: 'Vous devez renseigner le Fournisseur.',
            },
            events: {
                onClear: (formContext: any) => {
                    formContext.setValue('idFacturante', '');
                },
            },
            getOptionLabel: (item: Fournisseur) => `${item.id}` || '',
            // Se rends dans les options
            simpleRenderOption: (item: Fournisseur) => `${item.id}`,
            searchability: (terms: string) =>
                terms.length >= 2 ? true : 'Veuillez saisir au moins 2 caractères',
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12,
        }),
        [publisherDatasource, publisherFetchData],
    );

    const selectRejectCodeFormStructure: FormStructure = React.useMemo(() => {
        if (rejectCodeCompleteListDatasource.data.length === 0) {
            dispatch(rejectCodeCompleteListFetchData({}));
        }

        return {
            type: 'select',
            name: 'codeRejet',
            inputlabel: 'Code Rejet',
            options: rejectCodeCompleteListDatasource.data,
            rules: {
                required: 'Vous devez renseigner le Code Rejet.',
            },
            getOptionLabel: (item: CodeRejet) => `${item.code}` || '',
            getOptionValue: (item: CodeRejet) => `${item.code}`,
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12,
        };
    }, [dispatch, rejectCodeCompleteListDatasource.data, rejectCodeCompleteListFetchData]);

    const formatFrequencyLibelle = (dfrLibelle?: string | null) => {
        if (!dfrLibelle) return 'Pas de fréquence';

        if (dfrLibelle === 'Indéfini') {
            return 'Jamais';
        }

        return dfrLibelle;
    };

    const selectFrequencyFormStructure: FormStructure = React.useMemo(() => {
        if (frequencyRelanceAchatsDatasource.data.length === 0) {
            dispatch(frequencyRelanceAchatsFetchData({}));
        }

        return {
            type: 'select',
            name: 'idFrequenceRelance',
            inputlabel: 'Fréquence de relance',
            options: frequencyRelanceAchatsDatasource.data,
            getOptionLabel: (item: DefFrequence) => `${formatFrequencyLibelle(item.dfrLibelle)}`,
            getOptionValue: (item: DefFrequence) => `${item.dfrLibelle || 'Pas de libelle'}`,
            tooltip: 'Utile uniquement lorsque "Relance active" sera coché',
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12,
        };
    }, [dispatch, frequencyRelanceAchatsDatasource, frequencyRelanceAchatsFetchData]);

    const createExceptionFormStructure: Array<FormStructure> = React.useMemo(
        () => [
            {
                type: 'header',
                label: (
                    <HeaderStructure
                        icon={<FaOptionIcon {...publisherIcon.props} />}
                        title="Création d'une exception"
                    />
                ),
                sx: {
                    marginTop: '0px',
                },
                name: 'create_exception_header',
            },
            ...(isPublisherAlreadySelected ? [selectPublisherFormStructure] : []),
            ...[selectRejectCodeFormStructure],
            {
                type: 'boolean',
                name: 'relanceAuto',
                inputlabel: 'Relance active',
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
            },
            ...[selectFrequencyFormStructure],
        ],
        [
            isPublisherAlreadySelected,
            selectFrequencyFormStructure,
            selectPublisherFormStructure,
            selectRejectCodeFormStructure,
        ],
    );

    return (
        <FlexyForm
            onSubmit={handleSubmit}
            formObject={initialValues as unknown as FormObject}
            formStructure={createExceptionFormStructure}
        />
    );
};

export default CreateExceptionForm;
